import {
  limiterUnavailableMs,
  RequestCooldownService,
  retryAfterMs,
  retryDelayWithJitter,
} from './request-cooldown.service';
import {
  ListRoomsResponseSchema,
  RoomDescriptorSchema,
  RoleNoPasswordSchema,
  RolesNoPasswordSchema,
  GatewaysResponseSchema,
} from '@andor/contracts';
import { ApiCompatibilityService } from './api-compatibility.service';
import { StartupService as StartupApi } from '../../shared/api/generated/startup/startup.service';
import { RoomsService as RoomsApi } from '../../shared/api/generated/rooms/rooms.service';
import { AuthService as AuthApi } from '../../shared/api/generated/auth/auth.service';
import { PermissionsService as PermissionsApi } from '../../shared/api/generated/permissions/permissions.service';
import { GatewaysService as GatewaysApi } from '../../shared/api/generated/gateways/gateways.service';
import { RolesService as RolesApi } from '../../shared/api/generated/roles/roles.service';
import { BackendRecovery, MaintenanceService } from './maintenance.service';
import { PRESERVE_RECOVERY_IDENTITY, RECOVERY_GENERATION } from '../interceptors/recovery-context';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  from,
  switchMap,
  map,
  Observable,
  Subject,
  tap,
  takeUntil,
  retry,
  timer,
  timeout,
  throwError,
} from 'rxjs';
import { AppStore, IAppState } from '../../state/app.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { Breakpoints, TBreakpoints } from '../../shared/constants/breakpoints';
import { appConfigSchema } from '../../shared/schemas/settings';
import { RealtimeService, ServerEvent } from './realtime.service';

import {
  roleSchema,
  Role,
  GatewayResult,
  Startup,
  ReadyStartup,
  readyStartupSchema,
  neutralRole,
  AppType,
  DefaultRoleTransferResult,
  RoleWithPassword,
  GetRoleResponse,
  startupSchema,
  PrivilegesApi,
} from '../../shared/schemas/backend-api';
import { SessionService } from './session.service';
import { UserService } from './user.service';
import { PermissionStore } from '../../state/permissions.repository';
import { AppConfigStore } from '../../state/app-config.repository';
import { debug, error, info, warn } from '@4players/odin-common';
import { AppConfigService } from './app-config.service';
import { DeepLinkService } from './deep-link.service';
import { getRawOption, setRawOption } from '../../shared/helpers/cookie';
import { configDefault } from '../../shared/constants/app-config-default';
import { ParticipantSettingsStore } from '../../state/participant-settings.repository';
import { Room, RoomsStore, roomFromDescriptor } from '../../state/rooms.repository';
import { RoomPasswordStore } from '../../state/room-passwords.repository';
import { PasswordRecoveryService } from './password-recovery.service';
import { isTauri } from '../../shared/helpers/platform';
import { movedAppUrl } from '../../shared/helpers/moved-app';
import { activeSpace, renameSpace } from '../../shared/helpers/spaces';

declare global {
  // eslint-disable-next-line no-var
  var odin_debug_dump_state: () => JSON;
}

// `not-set-up`: the native app's tenant is unregistered or still needs its
// setup, both of which happen in the browser — the app has no setup wizard
// or promo page.
export type StartupError = 'connection' | 'invalid-response' | 'configuration' | 'not-set-up';
export type StartupLifecycle =
  { status: 'loading' } | { status: 'error'; reason: StartupError } | { status: Startup['status'] };

/** Startup retries after `503 RATE_LIMIT_UNAVAILABLE`, each after `Retry-After`. */
const STARTUP_LIMITER_RETRIES = 3;

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private readonly compatibility = inject(ApiCompatibilityService);
  private readonly startupApi = inject(StartupApi);
  private readonly roomsApi = inject(RoomsApi);
  private readonly authApi = inject(AuthApi);
  private readonly permissionsApi = inject(PermissionsApi);
  private readonly gatewaysApi = inject(GatewaysApi);
  private readonly rolesApi = inject(RolesApi);

  readonly maintenance = inject(MaintenanceService);
  readonly cooldown = inject(RequestCooldownService);
  #appConfigService = inject(AppConfigService);
  #realtime = inject(RealtimeService);
  #session = inject(SessionService);
  #roomsStore = inject(RoomsStore);
  // True once `_startRealtime()` has dialed — identity changes before that
  // are picked up by the initial dial itself (it reads the current token).
  #realtimeStarted = false;
  #realtimeStart?: Promise<void>;
  #realtimeGeneration = 0;
  #initialization?: Promise<void>;
  #snapshots = new Subject<void>();
  #identityInitialized = false;
  #recoveryHandled = false;
  // In-flight guard for the WS-downgrade confirmation fetch (see
  // `#confirmPermissionsDowngrade`).
  #permissionsResyncInFlight = false;
  #roomPasswords = inject(RoomPasswordStore);
  #recovery = inject(PasswordRecoveryService);
  #lifecycle = signal<StartupLifecycle>({ status: 'loading' });
  readonly lifecycle = this.#lifecycle.asReadonly();
  readonly isReady = computed(() => this.#lifecycle().status === 'ready');
  readonly startupError = computed(() => {
    const state = this.#lifecycle();
    return state.status === 'error' ? state.reason : null;
  });
  // Flips true after the first `rooms-snapshot` arrives from the realtime
  // channel. Pre-join screens gate their `hasPassword` / new-room
  // classification on this — until the registry is trustworthy, treating an
  // unknown roomId as "new" would be wrong.
  private _firstSnapshotReceived = signal(false);
  firstSnapshotReceived = this._firstSnapshotReceived.asReadonly();
  // Flips true once `init()` has run to completion (success or failure). The
  // `persistAppState` auto-reconnect in `OdinService` awaits this so the
  // cipher branch sees the real backend `developerMode` instead of the
  // default `false`.
  private _initialized = new BehaviorSubject(false);
  private _appType = signal<AppType | null>(null);
  readonly appType = this._appType.asReadonly();
  private _wasInvited = new BehaviorSubject(false);
  wasInvited$ = this._wasInvited.asObservable();
  // Emits the roomId of a room that was just closed/deleted server-side (from
  // the `room-closed` realtime event). `OdinService` listens so it can tear down
  // the connection if the user is currently in that room.
  private readonly _roomClosed = new Subject<string>();
  readonly roomClosed$ = this._roomClosed.asObservable();
  // Passthrough of `DeepLinkService.autoJoin` — tells `App` to fire
  // an `OdinService.connect()` once initialization finishes, skipping the
  // manual room-login / preparation-area click-through.
  get autoJoin(): boolean {
    return this._deepLink.autoJoin;
  }
  // True while a password-recovery link is being handled (landing view shown
  // by `init()`); `App` must then leave `activeSection` alone instead of
  // routing to the pre-join screen.
  get hasPendingRecovery(): boolean {
    return this.#recovery.hasPendingLanding;
  }
  readonly showPromo = computed(() => this.#lifecycle().status === 'unregistered');
  readonly showSetupWizard = computed(() => this.#lifecycle().status === 'setup-required');
  private _activeBreakpoint = new BehaviorSubject<TBreakpoints>('small');
  readonly activeBreakpoint$ = this._activeBreakpoint.pipe(distinctUntilChanged());
  get activeBreakpoint() {
    return this._activeBreakpoint.value;
  }

  private _inviteLink = signal<string>('');
  inviteLink = this._inviteLink.asReadonly();

  private _authenticationState = signal<'logged-out' | 'logged-in' | 'loading'>('logged-out');
  authenticationState = this._authenticationState.asReadonly();
  private _authenticationError = signal<string | null>(null);
  authenticationError = this._authenticationError.asReadonly();
  private _roleName = signal<string>('Guest');
  roleName = this._roleName.asReadonly();
  /**
   * Returns the product name for the application.
   */
  get name(): string {
    return String(process.env['APP_NAME']);
  }

  /**
   * Returns a Unix timestamp representing the build date/time for the application.
   */
  get buildDate(): number {
    return parseInt(String(process.env['APP_BUILD']));
  }

  /**
   * Returns the current Git commit hash of the application.
   */
  get commit(): string {
    return String(process.env['APP_COMMIT']);
  }

  /**
   * Returns the current Git branch of the application.
   */
  get branch(): string {
    return String(process.env['APP_BRANCH']);
  }

  /**
   * Returns a long format version string for the application.
   */
  get version(): string {
    return `${this.shortVersion} [Revision: ${this.commit ? this.commit.substring(0, 7) : this.buildDate}]`;
  }

  /**
   * Returns a short format version string for the application.
   */
  get shortVersion(): string {
    return String(process.env['APP_VERSION']);
  }

  get wasInvited(): boolean {
    return this._wasInvited.value;
  }

  set wasInvited(wasInvited: boolean) {
    this._wasInvited.next(wasInvited);
  }

  constructor(
    private _appStore: AppStore,
    private _appConfigStore: AppConfigStore,
    private _userSettingsStore: UserSettingsStore,
    private _userService: UserService,
    private _permissionStore: PermissionStore,
    private _deepLink: DeepLinkService,
    private _participantSettingsStore: ParticipantSettingsStore,
    breakpointObserver: BreakpointObserver
  ) {
    // Deep-link entry values come from `DeepLinkService` (pure URL parser).
    // Translate them into app-state semantics here: a room in the path means
    // the user was invited; a userName overlays whatever was persisted, so
    // `/my-room?userName=alice` joins as alice without a prior local session.
    if (this._deepLink.roomId) {
      this._wasInvited.next(true);
      // The path segment is the server roomId; the human display name is not in
      // the link. When it points at a DIFFERENT room than last session, any
      // persisted `roomName` belongs to that old room — seed `roomName` with the
      // roomId so a stale name can't leak into the header / share links /
      // exports. A known room's real name overrides this once the registry
      // learns the room (`_syncRoomNameFromRegistry`) or the join response
      // echoes it back (`_enterRoom`); a quick-meeting room genuinely has
      // roomId === roomName, which is exactly this fallback.
      const roomIdChanged = this._deepLink.roomId !== this._userSettingsStore.value.roomId;
      this._userSettingsStore.update({
        roomId: this._deepLink.roomId,
        ...(roomIdChanged ? { roomName: this._deepLink.roomId } : {}),
      });
    }
    if (this._deepLink.userName) {
      this._userSettingsStore.update({ userName: this._deepLink.userName });
    }
    // Capture the cipher password from `#p=...` if the invite link carried
    // one, then scrub the fragment so a screen-shared / bookmarked URL stops
    // leaking it. The fragment never reached the server, but it's still
    // visible in the address bar and in window.history.
    if (this._deepLink.roomId && this._deepLink.password) {
      this.#roomPasswords.set(this._deepLink.roomId, this._deepLink.password);
      this.#scrubHash();
    }

    breakpointObserver
      .observe([Breakpoints.small, Breakpoints.medium, Breakpoints.large])
      .subscribe((result) => {
        if (result.breakpoints[Breakpoints.large]) {
          this._activeBreakpoint.next('large');
          return;
        }
        if (result.breakpoints[Breakpoints.medium]) {
          this._activeBreakpoint.next('medium');
          this._appStore.update({
            loginAreaLogoSize: 'large',
          });
          // The pin survives: the panel nav bar offers the column toggle from
          // medium up. Only small viewports, which have no pinned column, drop it.
          this._userSettingsStore.update({
            usersListExpanded: false,
          });
          return;
        }
        if (result.breakpoints[Breakpoints.small]) {
          this._activeBreakpoint.next('small');
          this._appStore.update({
            loginAreaLogoSize: 'small',
          });
          this._userSettingsStore.update({
            usersListExpanded: false,
            panelClassPinned: null,
          });
        }
      });

    this._userSettingsStore.colorMode$.subscribe((mode) => {
      if (mode === 'dark') {
        document.body.classList.add('dark');
        document.body.classList.remove('light');
      } else {
        document.body.classList.add('light');
        document.body.classList.remove('dark');
      }
    });

    this.#roomsStore.rooms$.subscribe((rooms) => {
      info('[rooms] store updated', { rooms });
    });

    // A session-token refresh that changed the uid means HTTP now acts as a
    // different identity than the open WebSocket — the server never re-keys
    // a socket, so permission pushes for the old uuid would keep arriving
    // (and wrongly demote this session). Re-dial and re-sync the role.
    this.#session.identityChanged$.subscribe(() => {
      if (!this.#realtimeStarted) return;
      void this.#onIdentityChanged();
    });

    this.maintenance.entered.subscribe(() => {
      this.#realtime.close();
      this.#realtimeStarted = false;
      this.#realtimeStart = undefined;
      this.#realtimeGeneration++;
      this._firstSnapshotReceived.set(false);
    });
    this.maintenance.resumed.subscribe(() => this._initialized.next(true));
    this.#realtime.serverEvents$.subscribe((event) => {
      if (this.isReady()) this._handleServerEvent(event);
    });
    this.#realtime.onReconnect$.subscribe(() => {
      if (!this.isReady()) return;
      this.getPermissions().subscribe({
        error: (e) => warn('failed to re-sync permissions after reconnect', { e }),
      });
    });

    this._handleInviteLink();
    this._syncRoomNameFromRegistry();
  }

  init(): Promise<void> {
    this.#initialization ??= this.#initialize().finally(() => {
      this.#initialization = undefined;
    });
    return this.#initialization;
  }

  async #initialize(): Promise<void> {
    if (!(await this.maintenance.check())) return;
    this.#lifecycle.set({ status: 'loading' });
    this._initialized.next(false);
    this._authenticationState.set('loading');
    try {
      const data = await firstValueFrom(this.fetchStartup());
      // A former address: go to the new one and stay in `loading` meanwhile.
      if (data.movedTo && this.#followMove(data.movedTo.domain)) return;
      this.#prepareIdentity(data.andorUUID);
      this.#applyStartup(data);
      this.#beginPasswordRecovery();
      if (this.isReady()) void this._startRealtime().catch(() => {});
    } catch (e) {
      if (this.maintenance.active()) return;
      error('failed to initialize app settings', { e });
      this.#failStartup(e);
    } finally {
      // Waiters must also check isReady(): completion includes failed startup.
      this._initialized.next(true);
    }
  }

  /** Refresh backend state without resetting the existing media session or view. */
  async restoreAfterMaintenance(recovery: BackendRecovery, roomId?: string): Promise<void> {
    await this.#initialization;
    const assertCurrent = () => {
      if (!this.maintenance.isRecoveryCurrent(recovery.generation))
        throw new DOMException('Canceled', 'AbortError');
    };
    assertCurrent();
    let restoringRealtime = false;
    try {
      await this.compatibility.check(recovery.signal);
      assertCurrent();
      const response = await firstValueFrom(
        this.startupApi
          .getStartup({
            observe: 'response',
            context: new HttpContext()
              .set(RECOVERY_GENERATION, recovery.generation)
              .set(PRESERVE_RECOVERY_IDENTITY, true),
          })
          .pipe(takeUntil(fromEvent(recovery.signal, 'abort')))
      );
      assertCurrent();
      const data = startupSchema.parse(response.body);
      if (roomId && (data.status !== 'ready' || data.andorUUID !== getRawOption('andorUUID'))) {
        throw new Error(
          'The existing meeting identity could not be restored. Media remains connected.'
        );
      }
      const token = response.headers.get('x-andor-uuid');
      if (token) this.#session.updateFromHeader(token);
      if (!roomId) this.#prepareIdentity(data.andorUUID);
      this.#applyStartup(data);
      if (!roomId) this.#beginPasswordRecovery();
      if (this.isReady()) {
        restoringRealtime = true;
        this._firstSnapshotReceived.set(false);
        const snapshot = firstValueFrom(
          this.#snapshots.pipe(takeUntil(fromEvent(recovery.signal, 'abort')))
        );
        this.#realtimeStarted = false;
        await Promise.all([snapshot, this._startRealtime(recovery.generation)]);
        assertCurrent();
        // OdinService's onConnected handler has already reissued presence for
        // the current media session (which may have changed while recovering).
      }
    } catch (cause) {
      assertCurrent();
      if (
        restoringRealtime ||
        roomId ||
        (cause instanceof HttpErrorResponse && [0, 429, 503].includes(cause.status))
      )
        throw cause;
      this.#failStartup(cause);
    }
  }

  /**
   * The app was renamed and this address only redirects now. Browsers load the
   * same page on the new domain; the native app moves its space and reloads.
   * Never used while restoring a live meeting after maintenance. Returns false
   * when there is nowhere else to go.
   */
  #followMove(domain: string): boolean {
    if (isTauri) {
      const space = activeSpace();
      if (!space || space.domain === domain) return false;
      renameSpace(space.domain, domain);
      location.reload();
      return true;
    }
    const target = movedAppUrl(location.href, domain);
    if (!target) return false;
    location.replace(target);
    return true;
  }

  #prepareIdentity(uuid: string): void {
    setRawOption('andorUUID', uuid);
    this._migrateClientUserIdToAndorUUID();
    this._migrateServerAvatarUrl();
    this._userService.createOwnUser();
    if (!this.#identityInitialized && this._deepLink.roomId && this._deepLink.breakoutRoomId) {
      this._userService.updateUserData({ breakoutRoomId: this._deepLink.breakoutRoomId });
    }
    this.#identityInitialized = true;
  }

  #failStartup(cause: unknown): void {
    const reason: StartupError =
      cause instanceof HttpErrorResponse
        ? cause.error?.code === 'APP_CONFIGURATION_INVALID'
          ? 'configuration'
          : 'connection'
        : 'invalid-response';
    this._appType.set(null);
    this.#recovery.available.set(false);
    this.handlePermissionsUpdate(neutralRole());
    this.#stopRealtime();
    this._appStore.update({ isConnectedView: false });
    this.#lifecycle.set({ status: 'error', reason });
  }

  /**
   * Entry via a password-recovery link (`/#recover=<token>`): scrub the token
   * from the address bar, take over the view with the confirmation landing
   * (overriding any `persistAppState`-restored section / connected view) and
   * look the token up. The link is only redeemed when the user presses
   * Continue there (see `RecoverPassword`). Ignored on an unconfigured app —
   * there is no role to sign in to.
   */
  #beginPasswordRecovery(): void {
    const token = this._deepLink.recoverToken;
    if (!token || this.#recoveryHandled) return;
    this.#recoveryHandled = true;
    this.#scrubHash();
    if (this.showSetupWizard() || this.showPromo()) {
      warn('recovery link ignored: the app is not configured');
      return;
    }
    this._appStore.update({
      activeSection: 'recover-password',
      isConnectedView: false,
      loginAreaLogoSize: this.getResponsiveLogoSize(),
    });
    this.#recovery.begin(token);
  }

  /**
   * Drop the URL fragment (`#p=…` invite password, `#recover=…` token) once it
   * has been read, so a screen-shared / bookmarked URL stops leaking it. The
   * fragment never reached the server, but it is still visible in the address
   * bar and in window.history.
   */
  #scrubHash(): void {
    try {
      const stripped = `${location.pathname}${location.search}`;
      window.history.replaceState(window.history.state, '', stripped);
    } catch {
      // replaceState can throw in restrictive embed contexts — non-fatal.
    }
  }

  whenInitialized(): Promise<void> {
    if (this._initialized.value) return Promise.resolve();
    return firstValueFrom(this._initialized.pipe(filter(Boolean))).then(() => undefined);
  }

  // One-time data fixup for installs that predate the server-issued
  // identity model. If a locally-generated `userId` is still hanging around
  // in user-settings AND it differs from the server's `andorUUID`, re-key
  // any participant-settings entry stored under that old id, then clear the
  // legacy slot so the migration doesn't run again.
  //
  // Note: settings keyed against this client by *other* clients (i.e.
  // entries OTHER users have for ME under my old userId) live on their
  // devices and can't be migrated from here. They become orphans and
  // rebuild as users interact post-rollout.
  private _migrateClientUserIdToAndorUUID() {
    const legacyUserId = this._userSettingsStore.value.userId;
    if (!legacyUserId) return;
    const andorUUID = getRawOption('andorUUID');
    if (!andorUUID || andorUUID === legacyUserId) {
      // No identity change to apply; just clear the slot so future boots
      // skip this entirely.
      this._userSettingsStore.update({ userId: '' });
      return;
    }
    const legacy = this._participantSettingsStore.get(legacyUserId);
    if (legacy) {
      const existing = this._participantSettingsStore.get(andorUUID);
      if (existing) {
        this._participantSettingsStore.update({ ...existing, ...legacy, id: andorUUID });
      } else {
        this._participantSettingsStore.add({ ...legacy, id: andorUUID });
      }
      this._participantSettingsStore.delete(legacyUserId);
    }
    this._userSettingsStore.update({ userId: '' });
  }

  // One-time cleanup for installs that predate local avatar generation: those
  // persisted a server avatar URL (…/v1/avatar/…) in user-settings. The default
  // avatar is now generated locally from the name, so clear that stale slot —
  // an empty value makes `resolveAvatar` fall back to the local default. A
  // future custom-avatar upload will repopulate this field with its own value.
  private _migrateServerAvatarUrl() {
    const avatar = this._userSettingsStore.value.avatar;
    if (avatar && avatar.includes('/v1/avatar/')) {
      this._userSettingsStore.update({ avatar: '' });
    }
  }

  private _startRealtime(recoveryGeneration?: number): Promise<void> {
    if (!this.isReady()) return Promise.resolve();
    if (this.#realtimeStart) return this.#realtimeStart;
    if (this.#realtimeStarted) return Promise.resolve();
    const generation = this.#realtimeGeneration;
    // Track an opening channel too, so a replacement identity cancels it.
    this.#realtimeStarted = true;
    const token = this.#session.sessionToken() || 'unauthorized';
    const connection =
      recoveryGeneration === undefined
        ? this.#realtime.connect(token)
        : this.#realtime.connect(token, recoveryGeneration);
    const pending = connection
      .catch((e) => {
        if (generation === this.#realtimeGeneration) this.#realtimeStarted = false;
        error('failed to start realtime channel', { e });
        throw e;
      })
      .finally(() => {
        if (this.#realtimeStart === pending) this.#realtimeStart = undefined;
      });
    this.#realtimeStart = pending;
    return pending;
  }

  #stopRealtime(): void {
    this.#realtimeGeneration++;
    this.#realtime.close();
    this.#realtimeStarted = false;
    this.#realtimeStart = undefined;
    this._firstSnapshotReceived.set(false);
    this.#roomsStore.setAll([]);
  }

  private _handleServerEvent(event: ServerEvent) {
    try {
      switch (event.type) {
        case 'settings':
        case 'settings_added':
        case 'settings_modified':
        case 'settings_removed':
          this.#appConfigService.handleSettingsUpdate(appConfigSchema.parse(event.data));
          break;
        case 'permissions':
        case 'permissions_added':
        case 'permissions_modified':
        case 'permissions_removed': {
          // WS pushes are advisory — the server resolves them against the
          // identity the socket was opened with, which can lag the HTTP
          // identity. Upgrades apply directly; a downgrade is confirmed over
          // HTTP first so a ghost push can't wrongly unmount privileged UI
          // (a real downgrade still lands via the authoritative response).
          const update = roleSchema.parse(event.data);
          if (this.#isDowngrade(update)) {
            this.#confirmPermissionsDowngrade(update);
          } else {
            this.handlePermissionsUpdate(update);
          }
          break;
        }
        case 'app-type':
          // Payment reported an upgrade or a trial change (see TrialService).
          this._appType.set(event.data);
          break;
        case 'rooms-snapshot':
          this.#roomsStore.setAll(event.data.rooms.map((r) => roomFromDescriptor(r)));
          this._firstSnapshotReceived.set(true);
          this.#snapshots.next();
          break;
        case 'room-added':
        case 'room-updated':
          // Both are idempotent upserts; `room-updated` typically just changes
          // `memberCount`, but carries the full descriptor so any future
          // metadata is applied too.
          this.#roomsStore.add(roomFromDescriptor(event.data));
          break;
        case 'room-closed':
          this.#roomsStore.remove(event.data.roomId);
          this._roomClosed.next(event.data.roomId);
          break;
      }
    } catch (err) {
      warn(`received unknown event from backend`, { err, event });
    }
  }

  private _handleInviteLink() {
    // Re-emit whenever the room name OR the password store mutates — so a
    // freshly generated password for the current room immediately surfaces
    // in the share link. `buildInviteLink` reads the password via
    // `RoomPasswordStore.get()`, which subscribes the effect to changes. It
    // also reads `UserService.ownUser()`, so moving in/out of a breakout room
    // re-emits the link with/without `?breakoutRoomId=`.
    const roomId = toSignal(this._userSettingsStore.roomId$, { initialValue: '' });
    effect(() => {
      const id = roomId();
      this._inviteLink.set(this.buildInviteLink(id));
    });
  }

  private _syncRoomNameFromRegistry() {
    // Turns the invite-link placeholder (roomName seeded with the raw roomId
    // in the constructor) into the real display name as soon as the registry
    // learns the room (rooms-snapshot / room-added / lookupRoom upsert) —
    // before the first join — and tracks live renames. A room missing from
    // the registry keeps the last known name. The stored name is read
    // untracked via `.value`; no registry write depends on roomName, so the
    // update cannot re-trigger the effect.
    const roomId = toSignal(this._userSettingsStore.roomId$, { initialValue: '' });
    const rooms = toSignal(this.#roomsStore.rooms$, { initialValue: [] as Room[] });
    effect(() => {
      const id = roomId();
      if (!id) return;
      const name = rooms().find((r) => r.roomId === id)?.name;
      if (!name || name === this._userSettingsStore.value.roomName) return;
      this._userSettingsStore.update({ roomName: name });
    });
  }

  /** Validate the wire payload without mutating application state. */
  fetchStartup(): Observable<Startup> {
    return defer(() => {
      let limiterRetries = 0;
      return from(this.compatibility.check()).pipe(
        switchMap(() => this.startupApi.getStartup()),
        map((body) => startupSchema.parse(body)),
        timeout(15_000),
        retry({
          delay: (error) => {
            const delay = retryAfterMs(error);
            if (delay !== null) {
              this.cooldown.capture('startup', error);
              return timer(retryDelayWithJitter(delay));
            }
            // A brief limiter outage asks for a retry. Give up after a few so a
            // lasting one still reaches the startup error and its Retry button.
            const unavailable = limiterUnavailableMs(error);
            if (unavailable !== null && limiterRetries++ < STARTUP_LIMITER_RETRIES) {
              return timer(retryDelayWithJitter(unavailable));
            }
            return throwError(() => error);
          },
        }),
        takeUntil(this.maintenance.entered)
      );
    });
  }

  /** Apply validated server state. Navigation belongs to the startup coordinator. */
  #applyStartup(data: Startup): void {
    this._appType.set(data.type);
    this.#recovery.available.set(data.passwordRecovery);
    this.handlePermissionsUpdate(data.permissions);
    let colorMode = configDefault.colorMode;
    switch (data.status) {
      case 'ready':
        colorMode = data.settings.colorMode;
        this._appConfigStore.commit(data.settings);
        break;
      case 'unregistered':
      case 'setup-required':
        this.#stopRealtime();
        this._appStore.update({ isConnectedView: false });
        break;
    }
    if (this._userSettingsStore.colorMode === null) {
      this._userSettingsStore.update({ colorMode });
    }
    this.#lifecycle.set(
      isTauri && data.status !== 'ready'
        ? { status: 'error', reason: 'not-set-up' }
        : { status: data.status }
    );
  }

  /** Route non-meeting landing states; returns true when it owns the view. */
  routeStartupLanding(): boolean {
    if (this.startupError() || this.#lifecycle().status === 'loading') return true;
    if (this.showPromo()) {
      this._appStore.update({ activeSection: 'show-promo' });
      return true;
    }
    if (this.showSetupWizard()) {
      this._appStore.update({ activeSection: 'setup' });
      return true;
    }
    return this.hasPendingRecovery;
  }

  /** A successful save supplies server-resolved settings and owner privileges. */
  completeSetup(startup: ReadyStartup): void {
    try {
      const data = readyStartupSchema.parse(startup);
      this.#prepareIdentity(data.andorUUID);
      this.#applyStartup(data);
      this._appStore.update(this.preJoinViewState(false));
      void this._startRealtime().catch(() => {});
    } catch (e) {
      this.#failStartup(e);
    }
  }

  /**
   * On-demand refresh of the rooms list via `GET /v1/rooms`. The WS only
   * pushes deltas after the initial snapshot, so the room-login "refresh"
   * button uses this to re-pull the authoritative list (with current member
   * counts).
   */
  async refreshRooms(): Promise<void> {
    const res = await firstValueFrom(this.roomsApi.listRooms());
    this.#roomsStore.setAll(
      ListRoomsResponseSchema.parse(res).rooms.map((r) => roomFromDescriptor(r))
    );
  }

  /**
   * Resolve a single room by id via `GET /v1/rooms/:roomId` and upsert it into
   * the local store. Used when a `roomId` (e.g. from an invite link) is not in
   * the synced room list — including hidden rooms, which are never broadcast —
   * so the UI can show its name/schedule before joining. Returns the room, or
   * `null` when it does not exist (404) or the lookup fails. Browse lists must
   * still filter hidden rooms out even after they are upserted here.
   */
  async lookupRoom(roomId: string): Promise<Room | null> {
    if (!roomId) return null;
    try {
      const descriptor = await firstValueFrom(this.roomsApi.getRoomById(roomId));
      const room = roomFromDescriptor(RoomDescriptorSchema.parse(descriptor));
      this.#roomsStore.add(room);
      return room;
    } catch {
      // 404 (unknown room) or a transport error — treat as "not found".
      return null;
    }
  }

  /**
   * Resolve an invite-link roomId to the room a join should target. A room
   * already in the store under that id wins immediately; otherwise the server
   * resolves it via `GET /v1/rooms/:roomId` ({@link lookupRoom}), which
   * matches the exact id first (covering hidden/instant rooms) and falls back
   * to a listed room whose NAME slugifies to the id — old invite links
   * carried the slugified room name as the id. Returns `null` when nothing
   * matches; callers then keep the open-on-demand behaviour
   * (`createIfMissing`), and `POST /v1/rooms/join` applies the same name
   * fallback server-side as a final net.
   *
   * When the server resolves to a DIFFERENT id (name match), the room target
   * in `UserSettingsStore` is retargeted and a cipher password stored under
   * the invite slug (from the `#p=` fragment) is re-keyed to the matched
   * room, so the e2e join gate, header and share links all point at the real
   * room.
   */
  async resolveInviteRoom(roomId: string): Promise<Room | null> {
    if (!roomId) return null;
    const byId = this.#roomsStore.rooms.find((r) => r.roomId === roomId);
    if (byId) return byId;
    const resolved = await this.lookupRoom(roomId);
    if (!resolved || resolved.roomId === roomId) return resolved;
    info(
      `invite roomId resolved by room name; [roomId: ${roomId}, target: ${resolved.roomId}, name: ${resolved.name}]`
    );
    const password = this.#roomPasswords.get(roomId);
    if (password) {
      this.#roomPasswords.set(resolved.roomId, password);
      this.#roomPasswords.remove(roomId);
    }
    // Don't clobber a room the user re-picked while the lookup was in flight
    // (e.g. via the prep-area recovery picker).
    if (this._userSettingsStore.value.roomId === roomId) {
      this._userSettingsStore.update({ roomId: resolved.roomId, roomName: resolved.name });
    }
    return resolved;
  }

  buildInviteLink(id: string, opts: { includeBreakoutRoom?: boolean } = {}): string {
    const { includeBreakoutRoom = true } = opts;
    const password = id ? this.#roomPasswords.get(id) : undefined;
    // Auto-include the sharer's current breakout room, but only on links for
    // the room the sharer is actually in — a link built for another room
    // (e.g. onboarding's per-created-room copy) must stay clean. Callers can
    // opt out entirely (`includeBreakoutRoom: false`): email/calendar shares
    // always use the plain room link, because breakout membership is
    // transient state that should not be baked into an invitation.
    const ownBreakoutRoomId = includeBreakoutRoom
      ? this._userService.ownUser()?.data.breakoutRoomId || null
      : null;
    const breakoutRoomId =
      id && id === this._userSettingsStore.value.roomId ? ownBreakoutRoomId : null;
    return this._deepLink.buildInviteLink(id, password, breakoutRoomId);
  }

  syncThemeConfig() {
    void this.#appConfigService.syncConfig();
  }

  login(password: string) {
    if (this.cooldown.remaining('roleLogin') || this._authenticationState() === 'loading') return;
    this._authenticationError.set(null);
    this._authenticationState.set('loading');
    this.authApi
      .signIn({
        password,
      })
      .pipe(
        map((response) => {
          const { data, error } = roleSchema.safeParse(response);
          if (error) throw new Error('Invalid permissions');
          return data;
        })
      )
      .subscribe({
        next: (permissions) => {
          this.handlePermissionsUpdate(permissions);
          info('authentication successful', permissions);
        },
        error: (e) => {
          error('authentication failed', e);
          this._authenticationState.set('logged-out');
          const throttled = this.cooldown.capture('roleLogin', e);
          this._authenticationError.set(throttled ? null : 'Failed to join team!');
        },
      });
  }

  async logout() {
    const request = this.authApi.signOut({});
    const result = await firstValueFrom(request);
    this.#recovery.clearRecoveredRole();
    if ('privileges' in result) this.handlePermissionsUpdate(roleSchema.parse(result));
  }

  getPermissions(): Observable<Role> {
    return this.permissionsApi.getPermissions().pipe(
      map((response) => {
        const { data, error } = roleSchema.safeParse(response);
        if (error) throw new Error('Invalid permissions');
        return data;
      }),
      tap((response) => {
        this.handlePermissionsUpdate(response);
      })
    );
  }

  /**
   * The server keyed the old WebSocket to the previous identity — re-dial
   * with the fresh token and re-fetch the effective role, so pushes and
   * privileges track the identity HTTP now acts as.
   */
  async #onIdentityChanged(): Promise<void> {
    this.#stopRealtime();
    // During startup, its coordinator will dial with the replacement token
    // once the response has been validated and applied.
    if (!this.isReady()) return;
    await this._startRealtime();
    if (!this.isReady()) return;
    this.getPermissions().subscribe({
      error: (e) => warn('failed to re-sync permissions after identity change', { e }),
    });
  }

  /** Whether the pushed update takes away a privilege or the membership this session currently holds. */
  #isDowngrade(update: Role): boolean {
    const current = this._permissionStore.value;
    const privileges = update.privileges as Partial<PrivilegesApi>;
    const privilegeLost = (Object.keys(privileges) as (keyof PrivilegesApi)[]).some(
      (key) => current[key] === true && privileges[key] === false
    );
    const isMember = update.isMember ?? !update.default;
    const membershipLost = this._authenticationState() === 'logged-in' && !isMember;
    return privilegeLost || membershipLost;
  }

  /**
   * Confirm a pushed downgrade over HTTP before applying it. The fetch's own
   * `tap` applies the authoritative role (never routed back through the WS
   * branch — structurally loop-free); only when the confirmation request
   * itself fails is the pushed payload applied after all (fail closed).
   * Overlapping downgrade pushes are dropped — the in-flight response
   * supersedes them.
   */
  #confirmPermissionsDowngrade(update: Role): void {
    if (this.#permissionsResyncInFlight) return;
    this.#permissionsResyncInFlight = true;
    this.getPermissions().subscribe({
      next: () => {
        this.#permissionsResyncInFlight = false;
      },
      error: (e) => {
        this.#permissionsResyncInFlight = false;
        warn('permissions re-sync failed; applying pushed downgrade', { e });
        this.handlePermissionsUpdate(update);
      },
    });
  }

  handlePermissionsUpdate(update: Role) {
    debug('updating app permissions', update);

    // "Logged in" means the server holds an explicit member entry for this
    // session. Older servers don't send isMember — fall back to the historic
    // proxy "my effective role is not the default role".
    const isMember = update.isMember ?? !update.default;
    this._authenticationState.set(isMember ? 'logged-in' : 'logged-out');
    this._userService.updateUserData({ role: update.id ?? update.name });
    this._roleName.set(update.name);
    this._permissionStore.update(update.privileges);
  }

  async getGateways(): Promise<GatewayResult> {
    const request = this.gatewaysApi.getGateways();

    const result = await firstValueFrom(request);
    return GatewaysResponseSchema.parse(result);
  }

  async getRoles() {
    const request = this.rolesApi.getRoles();

    return RolesNoPasswordSchema.parse(await firstValueFrom(request));
  }

  /**
   * Upsert a role. The endpoint creates when `id` is unknown and updates when
   * it exists, so creating means posting to a client-minted id (see
   * `SettingsPermissions.newRole()`, which uses `crypto.randomUUID()`).
   *
   * Creating requires a password; updating may omit it to leave the current one
   * in place. That distinction is expressed at the call site by typing the
   * payload as {@link CreateRoleRequest} rather than {@link RoleWithPassword}.
   */
  async saveRole(id: string, role: RoleWithPassword): Promise<GetRoleResponse> {
    const request = this.rolesApi.setRole(id, role);

    return RoleNoPasswordSchema.parse(await firstValueFrom(request));
  }

  async deleteRole(id: string) {
    const request = this.rolesApi.deleteRole(id, {});
    return await firstValueFrom(request);
  }

  /**
   * Transfer the default role to `roleId`. The server requires a disposition
   * for the previously-default role: a new password (its members are signed
   * out) or deletion. Omit `previousRole` only when no previous default
   * exists (the server rejects the transfer otherwise).
   */
  async transferDefaultRole(
    roleId: string,
    previousRole?: { password: string } | { delete: true }
  ): Promise<DefaultRoleTransferResult> {
    const request = this.permissionsApi.setDefaultRole({ roleId, previousRole });
    return await firstValueFrom(request);
  }

  close() {
    this.#realtime.close();
  }
  async sendLogs() {
    const pluginState = await globalThis.odin_debug_dump_state();
  }

  getResponsiveLogoSize(): 'small' | 'large' {
    if (this.activeBreakpoint === 'small') {
      return 'small';
    } else {
      return 'large';
    }
  }

  /**
   * Section + logo for the pre-join screen, chosen by entry point: a URL with a
   * `:roomId` (`wasInvited`) lands on the preparation area, otherwise on the
   * room-login screen. Shared by the normal prep-page render and the
   * failed-connect fallback so the two never drift.
   *
   * `invited` defaults to `wasInvited` (correct for pre-join callers). Pass an
   * explicit value when routing AFTER a successful join — `wasInvited` is
   * cleared on join, so post-join callers must supply the durable origin (the
   * immutable deep-link roomId) instead. See {@link showRemovedFromRoomFallback}.
   */
  preJoinViewState(invited: boolean = this.wasInvited): Partial<IAppState> {
    return invited
      ? { activeSection: 'preparation-area', loginAreaLogoSize: 'invisible' }
      : { activeSection: 'room-login', loginAreaLogoSize: this.getResponsiveLogoSize() };
  }

  /**
   * Abandon a failed (re)connect: drop the optimistic `isConnectedView` (which
   * a `persistAppState` reload restores as `true` and which gates the empty
   * conference shell) and return to the pre-join screen. Deliberately leaves
   * `connectionError` untouched so the error set by `OdinService` surfaces on
   * the room-login / preparation-area screen.
   */
  showFailedConnectFallback(): void {
    this._appStore.update({ isConnectedView: false, ...this.preJoinViewState() });
  }

  /**
   * Return to the pre-join screen after the user is removed from a room they had
   * already joined (e.g. it was closed/deleted server-side). Same as
   * {@link showFailedConnectFallback} but routes by the *durable* join origin
   * (the deep-link roomId) rather than `wasInvited`, which a successful join
   * clears — so an invite-link joiner correctly lands back on the
   * preparation-area, not room-login. Leaves `connectionError` intact.
   */
  showRemovedFromRoomFallback(): void {
    this._appStore.update({
      isConnectedView: false,
      ...this.preJoinViewState(!!this._deepLink.roomId),
    });
  }
}
